import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MdClose, MdViewCompact } from 'react-icons/md';
import { useRef, useState } from 'react';

// Reusable modal dialog component (v2).
// Controlled by the parent via the `open` and `onClose` props.
// The body content is provided as `children`, the header title via the
// `title` prop (with an optional `titleIcon` rendered to its left), and the
// footer via the `actions` prop (buttons aligned right). An optional
// `actionsSecondary` prop renders a free content area on the footer's left
// (e.g. badges, indicators or extra buttons), aligned left and vertically
// centered together with the actions.
const ModalDialogV2 = ({
  open,
  onClose,
  title = '',
  titleIcon,
  actions = [],
  actionsSecondary,
  maxWidth = 'md',
  maxHeight = '95vh',
  fullWidth = true,
  fullScreen = false,
  children,
  sxContent,
}) => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledBottom, setIsScrolledBottom] = useState(false);

  // Tracks whether the scrollable content is scrolled away from the top (so the
  // header shows a subtle shadow) and away from the bottom (so the actions bar
  // shows a subtle shadow separating it from the content above).
  const handleContentScroll = () => {
    const el = contentRef.current;
    if (el) {
      setIsScrolled(el.scrollTop > 0);
      // Content still hidden below the visible area (scroll not at the bottom).
      setIsScrolledBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    }
  };

  // Size the icon so its 1em glyph fills the title line-box height
  // (h4 font-size x line-height); width stays proportional (square glyph).
  const titleIconSize = `calc(${theme.typography.h4.fontSize} * ${theme.typography.h4.lineHeight})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'grey.200',
          p: 0,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          maxHeight,
          // Reduce the default 32px outer margin (MUI default) on small
          // screens so the modal uses almost the full viewport width.
          m: { xs: 0.5, sm: 4 },
        },
        // Keep the fullWidth paper's width in sync with the reduced margin.
        '& .MuiDialog-paperFullWidth': {
          width: { xs: 'calc(100% - 8px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
          transition: 'box-shadow 0.2s ease',
          boxShadow: isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {titleIcon && (
            <Box
              component="span"
              sx={{
                color: 'primary.light',
                fontSize: titleIconSize,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {titleIcon}
            </Box>
          )}
          <Typography variant="h3" color="primary" fontWeight={300}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Cerrar"
          sx={{
            color: 'primary.contrastText',
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <MdClose style={{ fontSize: '2rem' }} />
        </IconButton>
      </Box>

      {/* Content (only this area scrolls) */}
      <Box
        ref={contentRef}
        onScroll={handleContentScroll}
        px={1}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': {
            width: 14,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'grey.200',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.400',
            borderRadius: 4,
            border:1,
            borderColor: 'grey.200',
            '&:hover': { bgcolor: 'grey.600' },
          },
          ...sxContent,
        }}
      >
        {children}
      </Box>

      {/* Actions (two columns: optional left free content + right action buttons) */}
      {(actions.length > 0 || actionsSecondary) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            position: 'relative',
            zIndex: 1,
            transition: 'box-shadow 0.2s ease',
            boxShadow: isScrolledBottom
              ? '0 -2px 8px rgba(0, 0, 0, 0.2)'
              : 'none',
          }}
        >
          {/* Left column - free content area (only when provided via actionsSecondary) */}
          {actionsSecondary && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1,
                minWidth: 0,
              }}
            >
              {actionsSecondary}
            </Box>
          )}

          {/* Right column - action buttons aligned to the right */}
          {actions.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                ml: 'auto',
                minWidth: 0,
              }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                  disabled={action.disabled || false}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
};

// Content capsule meant to be used as the wrapper inside every ModalDialogV2
// grid item. It replaces the repetitive white rounded Box markup
// (<Box sx={{ backgroundColor: '#fff', borderRadius: 1.5 }}>) filling the cell
// width and height, so capsules sharing a row keep equal heights. Extra styles
// can be merged with the `sx` prop. An optional `title` renders a full-width
// section header (primary color, hairline divider below). The header only
// renders when `title` is provided. An optional `leadingIcon` renders a small
// icon (20px) to the LEFT of the title, matching the accordion section headers.
// Its color is inherited from the wrapper, so it is defined by the consumer. A
// decorative icon can be shown at its right end via
// `titleIcon` (default: MdViewCompact at 2rem); set `hideTitleIcon` to true to
// suppress it.
const Capsule = ({
  title,
  leadingIcon,
  titleIcon = <MdViewCompact />,
  hideTitleIcon = false,
  children,
  sx,
  sxContent,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        flexGrow: 1,
        backgroundColor: '#fff',
        borderRadius: 1.5,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {title && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
            borderBottom: 1,
            borderColor: 'grey.200',
            p: 1,
            px:2,
            gap: 2,
          }}
        >
          {leadingIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'inherit',
                fontSize: '1.25rem',
                lineHeight: 1,
              }}
            >
              {leadingIcon}
            </Box>
          )}
          <Typography
            variant="h4"
            color="primary"
            fontWeight={600}
            sx={{ lineHeight: 1}}
          >
            {title}
          </Typography>
          {titleIcon && !hideTitleIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'tertiary.light',
                fontSize: '2rem',
                ml: 'auto',
                lineHeight: 1,
              }}
            >
              {titleIcon}
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ p: 3, ...sxContent }}>
        {children}
      </Box>
    </Box>
  );
};

export { Capsule };

export default ModalDialogV2;
